import axios from "axios";
import { clearOrderSession, getOrderSession } from "../utils/orderAccountSession";

/**
 * API base URL. Set REACT_APP_API_BASE_URL to override (staging builds must).
 * Production default: https://api.emergencyqr.in/v1 (backend serves both /api and /v1).
 */
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/$/, "");
  }
  // CRA only ever reports "development", "test" or "production"; staging builds must set REACT_APP_API_BASE_URL.
  if (process.env.NODE_ENV === "production") {
    return "https://api.emergencyqr.in/v1";
  }
  return "http://localhost:5000/v1";
};

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

/** Best-effort JWT exp check so stale admin/owner tokens are not sent on every request. */
function jwtExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

// Add request interceptor for JWT auth with expiry check
api.interceptors.request.use(
  (config) => {
    // Preserve explicit Authorization (e.g. family_token on /family/* routes)
    if (config.headers?.Authorization || config.headers?.authorization) {
      config._explicitAuth = true;
      return config;
    }

    // Check for admin token first
    const adminToken = localStorage.getItem("admin_token");
    if (adminToken) {
      if (jwtExpired(adminToken)) {
        endAdminSession();
      } else {
        config.headers["Authorization"] = `Bearer ${adminToken}`;
        return config;
      }
    }

    // Check for customer token with expiry validation
    const customerToken = localStorage.getItem("locket_jwt");
    const tokenExpiry = localStorage.getItem("locket_jwt_expiry");
    
    if (customerToken) {
      const expiryTime = tokenExpiry ? parseInt(tokenExpiry, 10) : 0;
      const now = Date.now();
      const clientExpired = tokenExpiry && now >= expiryTime;
      const jwtPastDue = jwtExpired(customerToken);

      if (clientExpired || jwtPastDue) {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        window.dispatchEvent(new CustomEvent("tokenExpired"));
      } else {
        config.headers["Authorization"] = `Bearer ${customerToken}`;
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

function endAdminSession() {
  localStorage.removeItem("admin_token");
  delete api.defaults.headers.common["Authorization"];
  sessionStorage.setItem("admin_session_expired", "1");
  window.dispatchEvent(new CustomEvent("adminTokenExpired"));
}

// On 401, clear whichever token triggered the request and notify the app
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const isRevoked = data?.code === "TOKEN_REVOKED" || data?.error === "Token has been revoked";
    const isExpired = data?.code === "TOKEN_EXPIRED" || data?.error === "Token expired";

    const isLinked = data?.code === "ACCOUNT_LINKED";

    if (status === 401 && error.config?._explicitAuth) {
      // Explicit header = admin, checkout-account or family token; never touch the owner session for it
      const sent = String(error.config.headers?.Authorization || error.config.headers?.authorization || "");
      const orderToken = getOrderSession()?.token;
      const adminToken = localStorage.getItem("admin_token");
      if (orderToken && sent === `Bearer ${orderToken}` && (isRevoked || isExpired || isLinked)) {
        clearOrderSession();
      } else if (adminToken && sent === `Bearer ${adminToken}` && (isRevoked || isExpired || data?.code === "INVALID_TOKEN")) {
        endAdminSession();
      }
    } else if (status === 401 && (isRevoked || isExpired)) {
      // Clear whichever token was active for this request
      if (localStorage.getItem("admin_token")) {
        endAdminSession();
      } else {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        window.dispatchEvent(new CustomEvent("tokenExpired"));
      }
    }
    return Promise.reject(error);
  }
);

export default api;
